/**
 * ExternalCheckoutModal - Trade Loop v2.0 (Card 18, tasks A, C and C2)
 *
 * The pre-link disclosure, and the platform-specific way out of the app. This is the only place
 * in a native build from which a user can reach checkout, and it never navigates the WebView.
 *
 * ## The two platforms are deliberately different, and the asymmetry is the whole point
 *
 * **iOS gets a real tappable link.** Since the May 2025 Epic contempt ruling, Apple 3.1.1(a)
 * says entitlements "are not required for developers to include buttons, external links, or
 * other calls to action in their United States storefront apps" — no entitlement, no approval,
 * and 0% commission. There is no reason to degrade the experience.
 *
 * **Android gets a copy button and no tappable link.** Google's external content links program
 * is triggered by "interactive/clickable links that direct users outside the app", and its
 * mechanics confirm the scope: registering link URIs in Play Console, calling
 * `launchExternalLink()`, and a 10% fee attributed to a 24-hour window that starts at a link
 * click. A copy-to-clipboard button does none of that — nothing navigates, no browser is handed
 * off to, and there is no click to attribute. Post-Epic, Google also explicitly stopped
 * prohibiting developers from telling US users about outside pricing. So this is not an external
 * content link: no enrollment, no reporting, no service fee.
 *
 * ⚠️ That is the letter of the rule, and it is the reason Card 18's enrollment runbook is parked
 * rather than deleted. If Google broadens "external content link" to cover any steering
 * affordance, this button falls in scope and enrollment becomes mandatory — and linking without
 * enrollment risks suspension, not a warning.
 *
 * ## Rules this component encodes
 *
 * - **The URL is always visible as selectable text**, on both platforms. A copy button alone is
 *   a dead end when the copy silently fails, which is exactly what WebView clipboard access does
 *   when it lacks a secure context or a gesture.
 * - **The short URL is always shown.** `gotradeloop.com/go` is typeable in five seconds, needs no
 *   token, and works on a second device. It matters more than the button.
 * - **No QR code, no mailto:, no SMS.** None of them is a link either, but Apple's rejection
 *   patterns name "QR codes to payment pages" as a call-to-action violation, so it is a
 *   known-flagged shape that would contaminate a binary shipping to both stores.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Check, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { copyToClipboard, openInSystemBrowser } from '@/utils/externalLinkOut';
import { isIOS } from '@/utils/platform';

interface ExternalCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName: string;
  /** The tokenised checkout URL. Null while it is still being minted. */
  handoffUrl: string | null;
  /** Typeable, token-free fallback. */
  shortUrl: string;
  isPreparing: boolean;
  error: string | null;
}

export function ExternalCheckoutModal({
  isOpen,
  onClose,
  planName,
  handoffUrl,
  shortUrl,
  isPreparing,
  error
}: ExternalCheckoutModalProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  // iOS opens; Android copies. Nothing else branches on platform in this component.
  const usesTappableLink = isIOS;

  const handleOpen = () => {
    if (!handoffUrl) return;
    // Synchronous, straight off the click: WKWebView only routes window.open to the system
    // browser for a user-gesture-initiated open, and an await here would spend that gesture.
    const opened = openInSystemBrowser(handoffUrl);
    if (opened) {
      onClose();
    } else {
      // Never fall back to navigating the WebView. The copyable URL below is the fallback.
      setCopyState('failed');
    }
  };

  const handleCopy = async () => {
    if (!handoffUrl) return;
    const copied = await copyToClipboard(handoffUrl);
    setCopyState(copied ? 'copied' : 'failed');
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setCopyState('idle');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Continue on gotradeloop.com</DialogTitle>
          {/* The pre-link disclosure. Required by Google's program and good practice for Apple:
              the user is told where they are going before anything happens. */}
          <DialogDescription>
            Subscriptions to {planName} are purchased on our website. You'll finish signing up at
            gotradeloop.com in your browser, and your plan will be applied to this account
            automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isPreparing && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Preparing your secure checkout link...
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {handoffUrl && (
            <>
              {/* Always rendered, on both platforms. If the copy fails or the open is refused,
                  this is what the user reads off the screen. */}
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Your checkout link
                </p>
                <p className="select-all break-all rounded-lg bg-gray-50 border border-gray-200 p-3 font-mono text-xs text-gray-700">
                  {handoffUrl}
                </p>
                <p className="text-xs text-gray-500">
                  This link is valid for 15 minutes and can only be used once.
                </p>
              </div>

              {usesTappableLink ? (
                <Button onClick={handleOpen} className="w-full h-11">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in browser
                </Button>
              ) : (
                <Button onClick={handleCopy} variant="outline" className="w-full h-11">
                  {copyState === 'copied' ? (
                    <>
                      <Check className="mr-2 h-4 w-4 text-green-600" />
                      Link copied — paste it into your browser
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy link
                    </>
                  )}
                </Button>
              )}

              {copyState === 'failed' && (
                <p className="text-sm text-amber-700">
                  We couldn't do that automatically. Press and hold the link above to select and
                  copy it, or type the short address below into your browser.
                </p>
              )}

              {/* The typeable fallback. No token, so it works on any device — the user simply
                  signs in when they get there. */}
              <div className="rounded-lg border border-dashed border-gray-300 p-3">
                <p className="text-xs text-gray-500 mb-1">Or type this into any browser:</p>
                <p className="select-all font-mono text-sm font-semibold text-gray-900">
                  {shortUrl.replace(/^https?:\/\//, '')}
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="w-full sm:w-auto">
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
